import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { PaymentForm, CreditCard } from "react-square-web-payments-sdk";
import { api } from "@/lib/apiClient";
import { useCart, fmtPrice } from "@/context/CartContext";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

const Checkout = () => {
  const navigate = useNavigate();
  const { items, subtotal, clear } = useCart();
  const [fulfillment, setFulfillment] = useState("PICKUP");
  const [config, setConfig] = useState({
    square_app_id: process.env.REACT_APP_SQUARE_APP_ID || "",
    square_location_id: "",
  });
  const [processing, setProcessing] = useState(false);

  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    delivery_address: "",
    delivery_date: "",
    card_message: "",
  });

  useEffect(() => {
    api
      .get("/config/public")
      .then(({ data }) =>
        setConfig((c) => ({
          ...c,
          square_app_id: data.square_app_id || c.square_app_id,
          square_location_id: data.square_location_id,
        }))
      )
      .catch(() => {});
  }, []);

  const deliveryFee = fulfillment === "DELIVERY" ? 2500 : fulfillment === "SHIPPING" ? 1500 : 0;
  const total = subtotal + deliveryFee;

  const updateField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validateForm = () => {
    if (!form.customer_name || !form.customer_email || !form.customer_phone) {
      toast.error("Please complete your contact details.");
      return false;
    }
    if ((fulfillment === "DELIVERY" || fulfillment === "SHIPPING") && !form.delivery_address) {
      toast.error(fulfillment === "DELIVERY" ? "Please add a delivery address (Graham NC)." : "Please add a shipping address.");
      return false;
    }
    if (items.length === 0) {
      toast.error("Your cart is empty.");
      return false;
    }
    return true;
  };

  const handlePayment = async (token) => {
    if (!validateForm()) return;
    setProcessing(true);
    try {
      const { data } = await api.post("/checkout", {
        source_id: token.token,
        items: items.map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity,
          variant_id: i.variant_id || null,
          notes: i.notes || null,
        })),
        fulfillment_type: fulfillment,
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        delivery_address: form.delivery_address || null,
        delivery_date: form.delivery_date || null,
        card_message: form.card_message || null,
      });
      clear();
      navigate(`/order/${data.order_id}`, { state: data });
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.detail
          ? typeof err.response.data.detail === "string"
            ? err.response.data.detail
            : "Payment failed. Please try another card."
          : "Something went wrong. Please try again."
      );
    } finally {
      setProcessing(false);
    }
  };

  const squareReady =
    config.square_app_id &&
    config.square_location_id &&
    config.square_location_id !== "REPLACE_WITH_LOCATION_ID";

  if (items.length === 0) {
    return (
      <div className="lf-container py-32 text-center" data-testid="checkout-empty">
        <p className="font-serif text-3xl italic" style={{ color: "var(--lf-ink-soft)" }}>
          Your cart is empty.
        </p>
        <Link to="/shop" className="lf-link mt-6 inline-block">
          ← Browse the shop
        </Link>
      </div>
    );
  }

  return (
    <div className="lf-container py-12 md:py-20" data-testid="checkout-page">
      <Link to="/shop" className="lf-link inline-flex items-center gap-2" data-testid="checkout-back">
        <ArrowLeft size={14} /> Continue shopping
      </Link>
      <h1
        className="font-serif mt-8 leading-none"
        style={{
          fontSize: "clamp(2.5rem, 5vw, 4rem)",
          fontWeight: 300,
          color: "var(--lf-ink)",
        }}
      >
        Checkout.
      </h1>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        {/* LEFT: form */}
        <div className="lg:col-span-7 space-y-12">
          {/* Fulfillment */}
          <section>
            <span className="lf-eyebrow">1 · Fulfillment</span>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { id: "PICKUP",   label: "Pickup",   sub: "Free · at our studio" },
                { id: "DELIVERY", label: "Delivery", sub: "+$25 · Graham NC only" },
                { id: "SHIPPING", label: "Shipping", sub: "+$15 · nationwide" },
              ].map((opt) => {
                const active = fulfillment === opt.id;
                return (
                  <button
                    key={opt.id}
                    data-testid={`fulfillment-${opt.id.toLowerCase()}`}
                    onClick={() => setFulfillment(opt.id)}
                    className="text-left p-6 transition-all"
                    style={{
                      border: `1px solid ${active ? "var(--lf-gold)" : "var(--lf-border)"}`,
                      background: active ? "var(--lf-surface)" : "transparent",
                    }}
                  >
                    <div
                      className="font-serif text-2xl"
                      style={{ color: active ? "var(--lf-gold-dark)" : "var(--lf-ink)" }}
                    >
                      {opt.label}
                    </div>
                    <div className="lf-eyebrow mt-1" style={{ fontSize: "0.65rem" }}>
                      {opt.sub}
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Contact */}
          <section>
            <span className="lf-eyebrow">2 · Your details</span>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
              <label className="block sm:col-span-2">
                <span className="lf-eyebrow" style={{ fontSize: "0.65rem" }}>Full name</span>
                <input
                  data-testid="checkout-name"
                  className="lf-input"
                  value={form.customer_name}
                  onChange={(e) => updateField("customer_name", e.target.value)}
                  placeholder="Marie Hernández"
                />
              </label>
              <label className="block">
                <span className="lf-eyebrow" style={{ fontSize: "0.65rem" }}>Email</span>
                <input
                  data-testid="checkout-email"
                  type="email"
                  className="lf-input"
                  value={form.customer_email}
                  onChange={(e) => updateField("customer_email", e.target.value)}
                  placeholder="you@example.com"
                />
              </label>
              <label className="block">
                <span className="lf-eyebrow" style={{ fontSize: "0.65rem" }}>Phone</span>
                <input
                  data-testid="checkout-phone"
                  type="tel"
                  className="lf-input"
                  value={form.customer_phone}
                  onChange={(e) => updateField("customer_phone", e.target.value)}
                  placeholder="(336) 555-0145"
                />
              </label>
              {fulfillment === "DELIVERY" && (
                <label className="block sm:col-span-2">
                  <span className="lf-eyebrow" style={{ fontSize: "0.65rem" }}>Delivery address · Graham NC only</span>
                  <input
                    data-testid="checkout-address"
                    className="lf-input"
                    value={form.delivery_address}
                    onChange={(e) => updateField("delivery_address", e.target.value)}
                    placeholder="Street, Graham, NC"
                  />
                </label>
              )}
              {fulfillment === "SHIPPING" && (
                <label className="block sm:col-span-2">
                  <span className="lf-eyebrow" style={{ fontSize: "0.65rem" }}>Shipping address</span>
                  <input
                    data-testid="checkout-address"
                    className="lf-input"
                    value={form.delivery_address}
                    onChange={(e) => updateField("delivery_address", e.target.value)}
                    placeholder="Street, City, State, ZIP"
                  />
                </label>
              )}
              <label className="block">
                <span className="lf-eyebrow" style={{ fontSize: "0.65rem" }}>
                  {fulfillment === "DELIVERY" ? "Delivery date" : fulfillment === "SHIPPING" ? "Preferred ship-by date (optional)" : "Pickup date"}
                </span>
                <input
                  data-testid="checkout-date"
                  type="date"
                  className="lf-input"
                  value={form.delivery_date}
                  onChange={(e) => updateField("delivery_date", e.target.value)}
                />
              </label>
              <label className="block sm:col-span-2 mt-4">
                <span className="lf-eyebrow" style={{ fontSize: "0.65rem" }}>Card message (optional)</span>
                <textarea
                  data-testid="checkout-message"
                  className="lf-input"
                  rows={2}
                  value={form.card_message}
                  onChange={(e) => updateField("card_message", e.target.value)}
                  placeholder="A few sweet words…"
                />
              </label>
            </div>
          </section>

          {/* Payment */}
          <section>
            <span className="lf-eyebrow">3 · Payment</span>
            <p className="text-sm mt-3" style={{ color: "var(--lf-ink-soft)" }}>
              Secured by Square. Your card details never touch our servers.
            </p>

            <div className="mt-6">
              {squareReady ? (
                <PaymentForm
                  applicationId={config.square_app_id}
                  locationId={config.square_location_id}
                  cardTokenizeResponseReceived={(token) => handlePayment(token)}
                >
                  <CreditCard
                    buttonProps={{
                      isLoading: processing,
                      css: {
                        backgroundColor: "#C5A059",
                        color: "#fff",
                        fontFamily: "Outfit, sans-serif",
                        fontWeight: 500,
                        fontSize: "0.78rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.18em",
                        padding: "16px 28px",
                        borderRadius: "0",
                        "&:hover": { backgroundColor: "#A8843F" },
                      },
                    }}
                  >
                    {processing ? "Processing…" : `Pay ${fmtPrice(total)}`}
                  </CreditCard>
                </PaymentForm>
              ) : (
                <div
                  className="p-6"
                  data-testid="checkout-square-pending"
                  style={{ background: "var(--lf-surface)", border: "1px solid var(--lf-gold)" }}
                >
                  <p className="font-serif text-xl italic" style={{ color: "var(--lf-ink)" }}>
                    Square payments are almost wired up.
                  </p>
                  <p className="text-sm mt-3" style={{ color: "var(--lf-ink-soft)" }}>
                    Add your Square Location ID to enable live card processing. You can
                    still place an inquiry-style order below — we'll reach out to confirm
                    payment.
                  </p>
                  <button
                    data-testid="checkout-place-order-without-payment"
                    className="lf-btn-primary mt-6"
                    disabled={processing}
                    onClick={() => handlePayment({ token: "cnon:card-nonce-ok" })}
                  >
                    {processing ? "Placing…" : `Place order · ${fmtPrice(total)}`}
                  </button>
                </div>
              )}
            </div>
          </section>
        </div>

        {/* RIGHT: summary */}
        <aside className="lg:col-span-5">
          <div
            className="p-8 lg:sticky lg:top-28"
            style={{ background: "var(--lf-surface)", border: "1px solid var(--lf-border)" }}
            data-testid="checkout-summary"
          >
            <span className="lf-eyebrow">Order summary</span>
            <ul className="mt-6 space-y-5">
              {items.map((it, idx) => (
                <li
                  key={`${it.product_id}-${it.variant_id || ""}-${idx}`}
                  className="flex gap-4"
                  data-testid={`summary-item-${it.product_id}`}
                >
                  <div
                    className="flex-shrink-0"
                    style={{ width: 64, height: 80, background: "white", borderRadius: 10, overflow: "hidden" }}
                  >
                    <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1">
                    <p className="font-serif text-lg leading-tight">{it.name}</p>
                    <p className="text-xs mt-1" style={{ color: "var(--lf-ink-soft)" }}>
                      Qty {it.quantity}
                    </p>
                    {it.notes && (
                      <p className="text-xs mt-1.5 italic leading-snug" style={{ color: "var(--lf-ink-soft)" }}>
                        {it.notes}
                      </p>
                    )}
                  </div>
                  <span className="text-sm whitespace-nowrap" style={{ color: "var(--lf-gold-deep)" }}>
                    {fmtPrice(it.price * it.quantity)}
                  </span>
                </li>
              ))}
            </ul>

            <div className="mt-8 pt-5 border-t space-y-2" style={{ borderColor: "var(--lf-border)" }}>
              <div className="flex justify-between text-sm">
                <span style={{ color: "var(--lf-ink-soft)" }}>Subtotal</span>
                <span data-testid="summary-subtotal">{fmtPrice(subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span style={{ color: "var(--lf-ink-soft)" }}>
                  {fulfillment === "DELIVERY" ? "Delivery" : fulfillment === "SHIPPING" ? "Shipping" : "Pickup"}
                </span>
                <span data-testid="summary-fulfillment-fee">
                  {fulfillment === "DELIVERY" ? fmtPrice(2500) : fulfillment === "SHIPPING" ? fmtPrice(1500) : "Free"}
                </span>
              </div>
              <div
                className="flex justify-between items-baseline pt-3 mt-3 border-t"
                style={{ borderColor: "var(--lf-border)" }}
              >
                <span className="lf-eyebrow">Total</span>
                <span
                  className="font-serif text-3xl italic"
                  style={{ color: "var(--lf-gold-dark)" }}
                  data-testid="summary-total"
                >
                  {fmtPrice(total)}
                </span>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Checkout;
